import React, { FC, useEffect, useState } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import { States } from '@yandex-360/telemost-sdk/audio-diagnostics';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { getSoundForAudioOutputCheck } from '../../../../../../../../assets/sound-for-audio-output-check';
import { browser } from '../../../../../../../../helpers/browser-capabilities';
import { i18n } from '../../../../../../../../helpers/i18n';
import { useActions } from '../../../../../../../../hooks';
import {
    getAudioOutputDeviceSelector,
    selectIsAudioInputDiagnosticRun,
    setIsAudioOutputDiagnosticRunAction,
} from '../../../../../../../settings';
import { useOutputAudioHandlerContext } from '../../../../../../hooks/use-output-audio-handler';
import { getOutputDiagnosticBtnTitle } from '../../../../../utils';
import css from '../styles.css';
import { getOutputDiagnosticBtnIcon } from './get-output-diagnostic-btn-icon';

interface OutputDeviceDiagnosticProps {
    isMobile?: boolean;
    isDisabled?: boolean;
}

const LIMIT_PLAY_SOUND = 4;
let isSoundLoaded = false;

export const OutputDeviceDiagnosticButton: FC<OutputDeviceDiagnosticProps> = ({ isMobile, isDisabled }) => {
    const { setIsAudioOutputDiagnosticRun } = useActions({
        setIsAudioOutputDiagnosticRun: setIsAudioOutputDiagnosticRunAction,
    });
    const currentOutputDeviceId = useSelector(getAudioOutputDeviceSelector)?.deviceId;
    const isInputDiagnosticRun = useSelector(selectIsAudioInputDiagnosticRun);
    const [isLoadingAudioData, setIsLoadingAudioData] = useState<boolean>(!isSoundLoaded);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const { audioHandlerRef, audioHandlerState } = useOutputAudioHandlerContext();

    useEffect(() => {
        if (!isSoundLoaded) {
            getSoundForAudioOutputCheck().then(() => {
                setIsLoadingAudioData(false);
                isSoundLoaded = true;
            });
        }
    }, []);

    const onClick = async () => {
        if (audioHandlerRef?.current) {
            switch (audioHandlerState) {
                case States.IDLE: {
                    const audioData = await getSoundForAudioOutputCheck();
                    isSoundLoaded = true;
                    audioHandlerRef.current.playFromBase64(
                        audioData,
                        currentOutputDeviceId,
                        false,
                        LIMIT_PLAY_SOUND,
                        !browser()?.isSafariBased()
                    );
                    setIsAudioOutputDiagnosticRun(true);
                    setIsLoadingAudioData(false);
                    break;
                }
                case States.PLAYING: {
                    audioHandlerRef.current.stopPlayback();
                    break;
                }
            }
        }
    };

    return (
        <div className={cn({ [css.isMobile]: isMobile, [css.diagnosticContainer]: isMobile })}>
            <TooltipStateful
                content={i18n('settings-v2', 'diagnostic-output-start-tooltip')}
                isDisabled={Boolean(audioHandlerState) && audioHandlerState !== States.IDLE}
                trigger="hover"
                className={css.inputDiagnosticButtonTooltip}
                placement="bottom right"
            >
                <Button
                    isDisabled={isInputDiagnosticRun || isDisabled}
                    isPending={!audioHandlerRef?.current || isLoadingAudioData}
                    className={css.diagnosticBtn}
                    onPress={onClick}
                    iconLeft={getOutputDiagnosticBtnIcon(audioHandlerState ?? States.IDLE, isHovered)}
                    onHoverChange={setIsHovered}
                >
                    {i18n('settings-v2', getOutputDiagnosticBtnTitle(audioHandlerState ?? States.IDLE, isHovered))}
                </Button>
            </TooltipStateful>
        </div>
    );
};
